"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Send, Sparkles, Zap } from "lucide-react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchAboutContacts, fetchClientStats } from "@/store/slices/landingSlice";
import Link from "next/link";

const CallToActionSection = () => {
  const dispatch = useAppDispatch();
  const { data: aboutData } = useAppSelector((state) => state.landing.aboutContact);
  const { data: statsData } = useAppSelector((state) => state.landing.clientStats);
  const { mode } = useAppSelector((state) => state.theme);
  const isDark = mode === "dark";
  const firstAbout = aboutData[0];

  React.useEffect(() => {
    dispatch(fetchAboutContacts());
    dispatch(fetchClientStats());
  }, [dispatch]);

  // Generate particle positions only on the client to avoid SSR/client mismatch
  const [particlePositions, setParticlePositions] = useState<{ x: number; y: number }[]>([]);
  useEffect(() => {
    const positions = Array.from({ length: 20 }, () => ({
      x: Math.random() * 1920,
      y: Math.random() * 1080,
    }));
    setParticlePositions(positions);
  }, []);

  const stats = [
    { value: statsData[1]?.value || "100+", label: statsData[1]?.label || "Projects Delivered" },
    { value: statsData[0]?.value || "300+", label: statsData[0]?.label || "Happy Clients" },
    { value: "98%", label: "Success Rate" },
    { value: "24/7", label: "Support Available" },
  ];

  return (
    <section
      className={`py-24 relative overflow-hidden transition-colors duration-700 ${isDark ? "bg-[#0B0F19]" : "bg-gradient-to-b from-slate-50 to-white"}`}
    >
      {/* Enhanced Animated Background */}
      <div className="absolute inset-0">
        <motion.div
          className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-lg filter blur-3xl ${isDark ? "bg-[#8B31FF] opacity-20" : "bg-[#8B31FF] opacity-[0.06]"}`}
          animate={{
            scale: [1, 1.3, 1],
            x: [0, 60, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-lg filter blur-3xl ${isDark ? "bg-[#8B31FF] opacity-20" : "bg-[#8B31FF] opacity-[0.06]"}`}
          animate={{
            scale: [1, 1.4, 1],
            x: [0, -60, 0],
            y: [0, -40, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className={`absolute top-1/2 right-1/3 w-64 h-64 rounded-lg filter blur-3xl ${isDark ? "bg-[#8B31FF] opacity-20" : "bg-[#8B31FF] opacity-[0.04]"}`}
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* Animated particles */}
      {particlePositions.map((position, i) => (
        <motion.div
          key={i}
          className={`absolute w-2 h-2 rounded-full ${isDark ? "bg-white/10" : "bg-[#8B31FF]/10"}`}
          initial={{
            x: position.x,
            y: position.y,
          }}
          animate={{
            y: [position.y, position.y - 100],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 2.5 + (i % 3),
            repeat: Infinity,
            delay: (i % 4) * 0.5,
          }}
        />
      ))}

      {/* Enhanced Grid Pattern Overlay */}
      <motion.div
        className={`absolute inset-0 ${isDark ? "opacity-10" : "opacity-[0.03]"}`}
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{
          duration: 30,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      >
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `linear-gradient(${isDark ? "rgba(255,255,255,0.1)" : "rgba(79,17,194,0.15)"} 1px, transparent 1px),
                            linear-gradient(90deg, ${isDark ? "rgba(255,255,255,0.1)" : "rgba(79,17,194,0.15)"} 1px, transparent 1px)`,
            backgroundSize: "50px 50px",
          }}
        />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Enhanced Floating Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`inline-flex items-center gap-2 backdrop-blur-sm border rounded-full px-6 py-2 mb-8 relative overflow-hidden ${
              isDark
                ? "bg-white/10 border-white/20"
                : "bg-[#8B31FF]/5 border-[#8B31FF]/20"
            }`}
          >
            {/* Shimmer effect */}
            <motion.div
              className={`absolute inset-0 skew-x-12 ${isDark ? "bg-gradient-to-r from-transparent via-white/20 to-transparent" : "bg-gradient-to-r from-transparent via-[#8B31FF]/10 to-transparent"}`}
              animate={{
                x: ["-100%", "200%"],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1,
              }}
            />
            <motion.div
              animate={{
                rotate: [0, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <Sparkles className="w-4 h-4 text-yellow-400" />
            </motion.div>
            <span
              className={`text-sm font-medium relative z-10 ${isDark ? "text-white" : "text-[#8B31FF]"}`}
            >
              Let&apos;s Create Something Amazing
            </span>
            <Zap className="w-4 h-4 text-[#8B31FF]" />
          </motion.div>

          {/* Main Heading with gradient animation */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-[1.12]"
          >
            <motion.span
              className={`bg-clip-text text-transparent ${
                isDark
                  ? "bg-gradient-to-r from-white via-[#8B31FF] to-white"
                  : "bg-gradient-to-r from-gray-900 via-[#8B31FF] to-gray-900"
              }`}
              animate={{
                backgroundPosition: ["0%", "100%", "0%"],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                backgroundSize: "200% auto",
              }}
            >
              {firstAbout?.mainTitle ||
                "Let's Build Something Incredible Together"}
            </motion.span>
          </motion.h2>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className={`text-base sm:text-lg md:text-xl mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}
          >
            {firstAbout?.subtitle ||
              "Ready to transform your ideas into reality? Our team of experts is here to help you succeed. Let's start your journey to digital excellence today."}
          </motion.p>

          {/* Enhanced CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <Link href="/contact">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="group cursor-pointer px-8 py-4 bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] cursor-pointer text-white rounded-lg font-semibold text-lg shadow-2xl flex items-center gap-2 relative overflow-hidden"
              >
                {/* Animated glow */}
                <motion.div className="absolute inset-0 bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Pulsing ring */}
                <motion.div
                  className="absolute inset-0 rounded-lg border-2 border-white/50"
                  animate={{
                    scale: [1, 1.2, 1.2, 1],
                    opacity: [0.5, 0, 0, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeOut",
                  }}
                />

                <span className="relative z-10">
                  {firstAbout?.ctaButtons[0]?.label || "Start Your Project"}
                </span>
                <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </Link>

            <Link href="/careers">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  y: -2,
                }}
                whileTap={{ scale: 0.95 }}
                className={`group px-8 py-4 backdrop-blur-sm cursor-pointer border-2 rounded-lg font-semibold text-lg flex items-center gap-2 transition-all font-sans relative overflow-hidden ${
                  isDark
                    ? "bg-white/10 border-white/30 text-white hover:border-white/50"
                    : "bg-white border-gray-300 text-gray-800 hover:border-[#8B31FF]/50 hover:bg-[#8B31FF]/5 shadow-sm"
                }`}
              >
                {/* Shimmer on hover */}
                <motion.div
                  className={`absolute inset-0 skew-x-12 opacity-0 group-hover:opacity-100 ${isDark ? "bg-gradient-to-r from-transparent via-white/10 to-transparent" : "bg-gradient-to-r from-transparent via-[#8B31FF]/5 to-transparent"}`}
                  animate={{
                    x: ["-100%", "200%"],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    repeatDelay: 0.5,
                  }}
                />
                <Send className="w-5 h-5 group-hover:rotate-45 transition-transform relative z-10" />
                <span className="relative z-10">
                  {firstAbout?.ctaButtons[1]?.label || "Contact Us"}
                </span>
              </motion.button>
            </Link>
          </motion.div>

          {/* Enhanced Stats with animations */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className={`grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 border-t ${isDark ? "border-white/10" : "border-gray-200"}`}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{
                  delay: 1 + index * 0.1,
                  type: "spring",
                  stiffness: 200,
                }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="text-center group relative"
              >
                {/* Glow effect on hover */}
                <motion.div
                  className={`absolute inset-0 rounded-lg blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${isDark ? "bg-gradient-to-br from-[#8B31FF]/30 to-[#8B31FF]/30" : "bg-gradient-to-br from-[#8B31FF]/10 to-[#8B31FF]/10"}`}
                />

                <div className="relative">
                  <motion.h3
                    className="text-3xl md:text-4xl font-bold mb-2"
                    style={{
                      backgroundImage: isDark
                        ? "linear-gradient(135deg, #ffffff 0%, #8B31FF 100%)"
                        : "linear-gradient(135deg, #8B31FF 0%, #8B31FF 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                    whileHover={{
                      scale: 1.1,
                    }}
                  >
                    {stat.value}
                  </motion.h3>
                  <p
                    className={`text-sm font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {stat.label}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div
        className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#8B31FF] to-transparent ${isDark ? "opacity-50" : "opacity-20"}`}
      />
    </section>
  );
};

export default CallToActionSection;
